import { cn } from '@/lib/utils'

export type StatusVariant =
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'default'

export interface StatusBadgeProps {
  status: string
  variant?: StatusVariant
  showDot?: boolean
  className?: string
}

// Status mapping configuration
const statusMap: Record<string, StatusVariant> = {
  // Success states
  delivered: 'success',
  active: 'success',
  completed: 'success',
  successful: 'success',
  approved: 'success',
  confirmed: 'success',
  published: 'success',
  online: 'success',

  // Warning states
  pending: 'warning',
  processing: 'warning',
  review: 'warning',
  waiting: 'warning',
  scheduled: 'warning',
  draft: 'warning',

  // Danger states
  failed: 'danger',
  reversed: 'danger',
  rejected: 'danger',
  declined: 'danger',
  inactive: 'danger',
  cancelled: 'danger',
  expired: 'danger',
  error: 'danger',
  offline: 'danger',

  // Info states
  new: 'info',
  updated: 'info',
  modified: 'info',

  // Order statuses (display format)
  'out for delivery': 'info',
  'handoff to courier': 'info',
  'ready for pickup': 'warning',
  accepted: 'warning',
}

const variantStyles: Record<StatusVariant, string> = {
  success: 'bg-green-50 text-green-700 border-green-200',
  warning: 'bg-amber-50 text-amber-700 border-amber-200',
  danger: 'bg-red-50 text-red-700 border-red-200',
  info: 'bg-blue-50 text-blue-700 border-blue-200',
  default: 'bg-gray-50 text-gray-700 border-gray-200',
}

const dotStyles: Record<StatusVariant, string> = {
  success: 'bg-green-500',
  warning: 'bg-amber-500',
  danger: 'bg-red-500',
  info: 'bg-blue-500',
  default: 'bg-gray-500',
}

export function StatusBadge({
  status,
  variant,
  showDot = true,
  className,
}: StatusBadgeProps) {
  // Determine variant from status if not explicitly provided
  const resolvedVariant =
    variant || statusMap[status?.toLowerCase()] || 'default'

  // Capitalize first letter of status
  const displayStatus =
    status?.charAt(0)?.toUpperCase() + status?.slice(1)?.toLowerCase() || 'N/A'

  return (
    <span
      className={cn(
        'inline-flex items-center gap-2 px-3 py-1 rounded-sm text-xs font-medium border tracking-wide',
        variantStyles[resolvedVariant],
        className
      )}
    >
      {showDot && (
        <span
          className={cn('w-2 h-2 rounded-full', dotStyles[resolvedVariant])}
        />
      )}
      {displayStatus}
    </span>
  )
}
